import { useCallback, useMemo, useState } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { useSendTransaction } from '@privy-io/react-auth/solana';
import { 
  Connection, 
  PublicKey,
  SystemProgram,
  SYSVAR_INSTRUCTIONS_PUBKEY,
  Ed25519Program,
  ComputeBudgetProgram,
  LAMPORTS_PER_SOL,
} from '@solana/web3.js';
import { Program, AnchorProvider, BN } from '@coral-xyz/anchor';
import { solanaUtils } from '../utils/solanaUtils';
import { waitForTransactionConfirmation } from '../utils/transactionParser';
import MoonRunnerIDL from '../idl/moon_runner.json';
import type { SignedRaceDetails } from '../services/GameApiService';
import type { SignedResults } from '../services/BettingWebSocketService';

const MOON_RUNNER_PROGRAM_ID = new PublicKey(MoonRunnerIDL.address);

export interface GameTransactionResult {
  success: boolean;
  signature?: string;
  error?: string;
}

/**
 * Hook for X-Racer game transactions (place_bet, continue_streak, cash_out)
 */
export const useGameTransactions = (solanaWallet: any) => {
  const { authenticated } = usePrivy();
  const { sendTransaction } = useSendTransaction();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Check wallet type
  const isEmbeddedWallet = solanaWallet?.walletClientType === 'privy';
  const isExternalWallet = solanaWallet?.walletClientType === 'phantom' || 
                           solanaWallet?.connectorType === 'solana_adapter';
  
  const connection = useMemo(
    () => new Connection(solanaUtils.getRpcEndpoint(), 'confirmed'), 
    []
  );

  /**
   * Get Moon Runner PDAs
   */
  const getPDAs = useCallback((userWallet: PublicKey, roundId?: string) => {
    // Protocol PDA: seeds = ["protocol"]
    const [protocolPDA] = PublicKey.findProgramAddressSync(
      [Buffer.from("protocol")],
      MOON_RUNNER_PROGRAM_ID
    );

    // UserState PDA: seeds = ["user", user_pubkey]
    const [userStatePDA] = PublicKey.findProgramAddressSync(
      [Buffer.from("user"), userWallet.toBuffer()],
      MOON_RUNNER_PROGRAM_ID
    );

    // Token PDA: seeds = ["token", creator, round_id]
    // round_id must be u64 (8 bytes little-endian), NOT a string
    let tokenPDA: PublicKey | undefined;
    if (roundId) {
      const roundIdBuffer = Buffer.alloc(8);
      roundIdBuffer.writeBigUInt64LE(BigInt(roundId));
      
      [tokenPDA] = PublicKey.findProgramAddressSync(
        [
          Buffer.from("token"),
          userWallet.toBuffer(),
          roundIdBuffer  // Use u64 buffer, not string
        ],
        MOON_RUNNER_PROGRAM_ID
      );
    }

    return { protocolPDA, userStatePDA, tokenPDA };
  }, []);

  /**
   * Create Ed25519 signature verification instruction for race details
   * MUST match contract's SignedRaceDetails::to_bytes() format EXACTLY
   */
  const createEd25519Instruction = useCallback((
    signedRace: SignedRaceDetails,
    adminPubkey: PublicKey
  ) => {
    // Reconstruct message that was signed
    const buffers: Buffer[] = [];
    
    // Round ID (u64 little-endian)
    const roundIdBuf = Buffer.alloc(8);
    roundIdBuf.writeBigUInt64LE(BigInt(signedRace.round_id), 0);
    buffers.push(roundIdBuf);
    
    // Start timestamp (i64 little-endian)
    const startBuf = Buffer.alloc(8);
    startBuf.writeBigInt64LE(BigInt(signedRace.start_timestamp), 0);
    buffers.push(startBuf);
    
    // End timestamp (i64 little-endian)
    const endBuf = Buffer.alloc(8);
    endBuf.writeBigInt64LE(BigInt(signedRace.end_timestamp), 0);
    buffers.push(endBuf);
    
    // Cars with odds
    for (const { car, odds } of signedRace.cars_with_odds) {
      // Car name as UTF-8 bytes
      buffers.push(Buffer.from(car, 'utf-8'));
      // Null terminator
      buffers.push(Buffer.from([0x00]));
      // Odds (u64 little-endian)
      const oddsBuf = Buffer.alloc(8);
      oddsBuf.writeBigUInt64LE(BigInt(odds), 0);
      buffers.push(oddsBuf);
    }
    
    const message = Buffer.concat(buffers);
    const signature = Buffer.from(signedRace.signature);

    return Ed25519Program.createInstructionWithPublicKey({
      publicKey: adminPubkey.toBytes(),
      message,
      signature,
    });
  }, []);

  /**
   * Create Ed25519 signature verification instruction for race results
   * MUST match contract's SignedResults::to_bytes() format EXACTLY
   */
  const createEd25519InstructionForResults = useCallback((
    signedResults: SignedResults,
    adminPubkey: PublicKey
  ) => {
    // Reconstruct message that was signed
    // MUST match backend's _createResultsMessage format EXACTLY
    const buffers: Buffer[] = [];
    
    // Round ID (u64 little-endian)
    const roundIdBuf = Buffer.alloc(8);
    roundIdBuf.writeBigUInt64LE(BigInt(signedResults.round_id), 0);
    buffers.push(roundIdBuf);
    
    // Results array (NO LENGTH PREFIX - backend doesn't include it!)
    // Each result string (UTF-8 bytes + null terminator)
    for (const result of signedResults.results) {
      buffers.push(Buffer.from(result, 'utf-8'));
      buffers.push(Buffer.from([0x00])); // Null terminator
    }
    
    // Timestamp (i64 little-endian)
    const timestampBuf = Buffer.alloc(8);
    timestampBuf.writeBigInt64LE(BigInt(signedResults.timestamp), 0);
    buffers.push(timestampBuf);
    
    const message = Buffer.concat(buffers);
    const signature = Buffer.from(signedResults.signature);

    return Ed25519Program.createInstructionWithPublicKey({
      publicKey: adminPubkey.toBytes(),
      message,
      signature,
    });
  }, []);

  /**
   * Launch token (pick asset) on-chain using Moon Runner contract
   * 
   * @param signedRaceDetails - From currentRound.signed_race_details
   * @param carPick - Asset picked (BTC, SOL, etc.)
   * @param contributionSol - Contribution amount in SOL
   * @returns Transaction signature
   */
  const launchToken = useCallback(async (
    signedRaceDetails: SignedRaceDetails,
    carPick: string,
    contributionSol: number
  ): Promise<GameTransactionResult> => {
    setIsLoading(true);
    setError(null);

    if (!authenticated || !solanaWallet?.address) {
      const err = 'Wallet not connected';
      setError(err);
      setIsLoading(false);
      return { success: false, error: err };
    }

    try {
      const userWallet = new PublicKey(solanaWallet.address);
      
      // Get admin pubkey - MUST match backend admin wallet
      const adminPubkey = new PublicKey(
        process.env.REACT_APP_ADMIN_PUBLIC_KEY || 
        '6W2V5ckFfM5zGjUTG17H15PbHfNFZpz3x8qJjeiAPmeK'
      );
      
      const { protocolPDA, userStatePDA, tokenPDA } = getPDAs(userWallet, signedRaceDetails.round_id.toString());
      
      if (!tokenPDA) {
        throw new Error('Failed to derive Token PDA');
      }
      
      const contributionLamports = Math.floor(contributionSol * LAMPORTS_PER_SOL);

      // Create Ed25519 signature verification instruction
      const ed25519Ix = createEd25519Instruction(signedRaceDetails, adminPubkey);

      // Create program instance
      const provider = new AnchorProvider(
        connection,
        // @ts-ignore - minimal wallet adapter interface
        {
          publicKey: userWallet,
          signTransaction: async (tx: any) => tx,
          signAllTransactions: async (txs: any[]) => txs,
        },
        { commitment: 'confirmed' }
      );

      const program = new Program(MoonRunnerIDL as any, provider);

      // Build launch_token transaction
      const transaction = await program.methods
        .launchToken(
          {
            roundId: new BN(signedRaceDetails.round_id),
            startTimestamp: new BN(signedRaceDetails.start_timestamp),
            endTimestamp: new BN(signedRaceDetails.end_timestamp),
            carsWithOdds: signedRaceDetails.cars_with_odds.map(co => ({
              car: co.car,
              odds: new BN(co.odds)
            })),
            signature: signedRaceDetails.signature
          },
          carPick,
          new BN(contributionLamports)
        )
        .accounts({
          protocol: protocolPDA,
          userState: userStatePDA,
          token: tokenPDA,
          user: userWallet,
          systemProgram: SystemProgram.programId,
          instructions: SYSVAR_INSTRUCTIONS_PUBKEY,
        })
        .preInstructions([
          // Compute budget for priority
          ComputeBudgetProgram.setComputeUnitLimit({ units: 200_000 }),
          ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 375_000 }),
          // Ed25519 signature verification
          ed25519Ix
        ])
        .transaction();

      // Set recent blockhash and fee payer
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = userWallet;


      // Simulate transaction before signing (Phantom best practice)
      // This catches on-chain errors early and prevents wallet warnings
      try {
        // For legacy transactions, we need to populate the signature slots before simulation
        // The actual signatures don't matter for simulation, just the structure
        transaction.signatures = transaction.signatures.map(sig => ({
          ...sig,
          signature: null
        }));
        
        const simulation = await connection.simulateTransaction(transaction);
        
        if (simulation.value.err) {
          console.error('Transaction simulation failed:', simulation.value.err, simulation.value.logs);
          throw new Error(`Transaction will fail: ${JSON.stringify(simulation.value.err)}`);
        }
      } catch (simError: any) {
        throw new Error(`Pre-flight simulation failed: ${simError.message}`);
      }

      // Sign and send transaction
      let signature: string;

      // For external wallets (Phantom), use separate sign/send pattern
      if (isExternalWallet && solanaWallet?.signTransaction) {
        try {
          const signedTx = await solanaWallet.signTransaction(transaction);
          signature = await connection.sendRawTransaction(signedTx.serialize(), {
            skipPreflight: true,  // Already simulated above
            preflightCommitment: 'confirmed',
            maxRetries: 3
          });
        } catch (walletError: any) {
          console.error('Wallet signing failed:', walletError);
          throw new Error(`Wallet signing failed: ${walletError.message || 'Unknown error'}`);
        }
      } else if (isEmbeddedWallet) {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: true },
          transactionOptions: {
            skipPreflight: true,  // Already simulated above
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      } else {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: false },
          transactionOptions: {
            skipPreflight: true,  // Already simulated above
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      }

      // Wait for confirmation
      const confirmed = await waitForTransactionConfirmation(connection, signature);
      if (!confirmed) {
        throw new Error('Transaction failed to confirm');
      }

      setIsLoading(false);
      return {
        success: true,
        signature
      };

    } catch (error: any) {
      console.error('❌ Launch token failed:', error);
      const errMsg = error.message || 'Failed to launch token on-chain';
      setError(errMsg);
      setIsLoading(false);
      return {
        success: false,
        error: errMsg
      };
    }
  }, [
    authenticated,
    solanaWallet,
    connection,
    sendTransaction,
    isEmbeddedWallet,
    isExternalWallet,
    getPDAs,
    createEd25519Instruction
  ]);

  /**
   * Continue streak (settle previous round + launch new token) on-chain using Moon Runner contract
   * Used for round 2+ when user picks an asset after winning previous round
   * 
   * ONE transaction with TWO instructions:
   * 1. distribute_token (settle previous round, auto-sell)
   * 2. launch_token (start new round with winnings)
   * 
   * @param signedResults - Results from PREVIOUS round (for settlement)
   * @param signedRaceDetails - Race details for NEW round (for new bet)
   * @param carPick - Asset picked for new round
   * @param contributionSol - Contribution amount in SOL (initial_bet × current_multiplier)
   * @returns Transaction signature
   */
  const continueStreak = useCallback(async (
    signedResults: SignedResults,
    signedRaceDetails: SignedRaceDetails,
    carPick: string,
    contributionSol: number
  ): Promise<GameTransactionResult> => {
    setIsLoading(true);
    setError(null);

    if (!authenticated || !solanaWallet?.address) {
      const err = 'Wallet not connected';
      setError(err);
      setIsLoading(false);
      return { success: false, error: err };
    }

    try {
      const userWallet = new PublicKey(solanaWallet.address);
      
      // Get admin pubkey - MUST match backend admin wallet
      const adminPubkey = new PublicKey(
        process.env.REACT_APP_ADMIN_PUBLIC_KEY || 
        '6W2V5ckFfM5zGjUTG17H15PbHfNFZpz3x8qJjeiAPmeK'
      );
      
      // Get PDAs for BOTH previous and new rounds
      const previousRoundId = signedResults.round_id.toString();
      const newRoundId = signedRaceDetails.round_id.toString();
      
      const { protocolPDA, tokenPDA: previousTokenPDA } = getPDAs(userWallet, previousRoundId);
      const { userStatePDA, tokenPDA: newTokenPDA } = getPDAs(userWallet, newRoundId);
      
      if (!previousTokenPDA) {
        throw new Error('Failed to derive previous Token PDA');
      }
      if (!newTokenPDA) {
        throw new Error('Failed to derive new Token PDA');
      }
      
      const contributionLamports = Math.floor(contributionSol * LAMPORTS_PER_SOL);

      // Create Ed25519 instructions for BOTH results (previous round) and race (new round)
      const ed25519ResultsIx = createEd25519InstructionForResults(signedResults, adminPubkey);
      const ed25519RaceIx = createEd25519Instruction(signedRaceDetails, adminPubkey);

      // Create program instance
      const provider = new AnchorProvider(
        connection,
        // @ts-ignore - minimal wallet adapter interface
        {
          publicKey: userWallet,
          signTransaction: async (tx: any) => tx,
          signAllTransactions: async (txs: any[]) => txs,
        },
        { commitment: 'confirmed' }
      );

      const program = new Program(MoonRunnerIDL as any, provider);

      // Convert signedResults to Anchor format for distribute_token
      const distributeArgs = {
        roundId: new BN(signedResults.round_id.toString()),
        results: signedResults.results,
        timestamp: new BN(signedResults.timestamp.toString()),
        signature: Array.from(signedResults.signature)
      };

      // Build instruction 1: distribute_token (settle previous round)
      const distributeIx = await program.methods
        .distributeToken(distributeArgs, true) // auto_sell=true
        .accounts({
          protocol: protocolPDA,
          token: previousTokenPDA,
          user: userWallet,
          instructions: SYSVAR_INSTRUCTIONS_PUBKEY,
        })
        .instruction();

      // Create ONE transaction with BOTH instructions
      // The main instruction is launch_token, with distribute_token in preInstructions
      const transaction = await program.methods
        .launchToken(
          {
            roundId: new BN(signedRaceDetails.round_id),
            startTimestamp: new BN(signedRaceDetails.start_timestamp),
            endTimestamp: new BN(signedRaceDetails.end_timestamp),
            carsWithOdds: signedRaceDetails.cars_with_odds.map(co => ({
              car: co.car,
              odds: new BN(co.odds)
            })),
            signature: signedRaceDetails.signature
          },
          carPick,
          new BN(contributionLamports)
        )
        .accounts({
          protocol: protocolPDA,
          userState: userStatePDA,
          token: newTokenPDA,
          user: userWallet,
          systemProgram: SystemProgram.programId,
          instructions: SYSVAR_INSTRUCTIONS_PUBKEY,
        })
        .preInstructions([
          // Compute budget for priority (higher for 2 instructions)
          ComputeBudgetProgram.setComputeUnitLimit({ units: 400_000 }),
          ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 375_000 }),
          // Ed25519 signature verification for results (previous round)
          ed25519ResultsIx,
          // Ed25519 signature verification for race (new round)
          ed25519RaceIx,
          // Instruction 1: Distribute previous token (executes first)
          distributeIx
          // Instruction 2: Launch new token (main method call, executes second)
        ])
        .transaction();

      // Set recent blockhash and fee payer
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = userWallet;

      // Simulate transaction before signing
      try {
        transaction.signatures = transaction.signatures.map(sig => ({
          ...sig,
          signature: null
        }));
        
        const simulation = await connection.simulateTransaction(transaction);
        
        if (simulation.value.err) {
          console.error('Transaction simulation failed:', simulation.value.err, simulation.value.logs);
          throw new Error(`Transaction will fail: ${JSON.stringify(simulation.value.err)}`);
        }
      } catch (simError: any) {
        throw new Error(`Pre-flight simulation failed: ${simError.message}`);
      }

      // Sign and send transaction
      let signature: string;

      if (isExternalWallet && solanaWallet?.signTransaction) {
        try {
          const signedTx = await solanaWallet.signTransaction(transaction);
          signature = await connection.sendRawTransaction(signedTx.serialize(), {
            skipPreflight: true,
            preflightCommitment: 'confirmed',
            maxRetries: 3
          });
        } catch (walletError: any) {
          console.error('Wallet signing failed:', walletError);
          throw new Error(`Wallet signing failed: ${walletError.message || 'Unknown error'}`);
        }
      } else if (isEmbeddedWallet) {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: true },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      } else {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: false },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      }

      // Wait for confirmation
      const confirmed = await waitForTransactionConfirmation(connection, signature);
      if (!confirmed) {
        throw new Error('Transaction failed to confirm');
      }

      setIsLoading(false);
      return {
        success: true,
        signature
      };

    } catch (error: any) {
      console.error('❌ Continue streak failed:', error);
      const errMsg = error.message || 'Failed to continue streak on-chain';
      setError(errMsg);
      setIsLoading(false);
      return {
        success: false,
        error: errMsg
      };
    }
  }, [
    authenticated,
    solanaWallet,
    connection,
    sendTransaction,
    isEmbeddedWallet,
    isExternalWallet,
    getPDAs,
    createEd25519Instruction,
    createEd25519InstructionForResults
  ]);

  /**
   * Distribute token (cash out) on-chain using Moon Runner contract
   * 
   * @param signedResults - From currentRound.signed_results (after race settled)
   * @param autoSell - Whether to auto-sell tokens for SOL (default true)
   * @returns Transaction signature
   */
  const distributeToken = useCallback(async (
    signedResults: SignedResults,
    autoSell: boolean = true
  ): Promise<GameTransactionResult> => {
    setIsLoading(true);
    setError(null);

    if (!authenticated || !solanaWallet?.address) {
      const err = 'Wallet not connected';
      setError(err);
      setIsLoading(false);
      return { success: false, error: err };
    }

    try {
      const userWallet = new PublicKey(solanaWallet.address);
      
      // Get admin pubkey - MUST match backend admin wallet
      const adminPubkey = new PublicKey(
        process.env.REACT_APP_ADMIN_PUBLIC_KEY || 
        '6W2V5ckFfM5zGjUTG17H15PbHfNFZpz3x8qJjeiAPmeK'
      );
      
      // Need to get round_id from signed results to derive Token PDA
      const roundId = signedResults.round_id.toString();
      const { protocolPDA, tokenPDA } = getPDAs(userWallet, roundId);
      
      if (!tokenPDA) {
        throw new Error('Failed to derive Token PDA');
      }

      // Verify Token PDA exists before attempting distribution
      try {
        const tokenAccount = await connection.getAccountInfo(tokenPDA);
        if (!tokenAccount) {
          throw new Error('No token found on-chain. Cannot distribute.');
        }
      } catch (err) {
        console.error('❌ Error verifying token state:', err);
        throw err;
      }

      // Create Ed25519 signature verification instruction for results
      const ed25519Ix = createEd25519InstructionForResults(signedResults, adminPubkey);

      // Create program instance
      const provider = new AnchorProvider(
        connection,
        // @ts-ignore - minimal wallet adapter interface
        {
          publicKey: userWallet,
          signTransaction: async (tx: any) => tx,
          signAllTransactions: async (txs: any[]) => txs,
        },
        { commitment: 'confirmed' }
      );

      const program = new Program(MoonRunnerIDL as any, provider);

      // Build distribute_token transaction
      // Convert to Anchor-compatible format
      const distributeArgs = {
        roundId: new BN(signedResults.round_id.toString()),
        results: signedResults.results,
        timestamp: new BN(signedResults.timestamp.toString()),
        signature: Array.from(signedResults.signature)
      };
      
      const transaction = await program.methods
        .distributeToken(distributeArgs, autoSell)
        .accounts({
          protocol: protocolPDA,
          token: tokenPDA,
          user: userWallet,
          instructions: SYSVAR_INSTRUCTIONS_PUBKEY,
        })
        .preInstructions([
          // Compute budget for priority
          ComputeBudgetProgram.setComputeUnitLimit({ units: 200_000 }),
          ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 375_000 }),
          // Ed25519 signature verification for results
          ed25519Ix
        ])
        .transaction();

      // Set recent blockhash and fee payer
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = userWallet;


      // Simulate transaction before signing (Phantom best practice)
      try {
        // For legacy transactions, we need to populate the signature slots before simulation
        // The actual signatures don't matter for simulation, just the structure
        transaction.signatures = transaction.signatures.map(sig => ({
          ...sig,
          signature: null
        }));
        
        const simulation = await connection.simulateTransaction(transaction);
        
        if (simulation.value.err) {
          console.error('Transaction simulation failed:', simulation.value.err, simulation.value.logs);
          throw new Error(`Transaction will fail: ${JSON.stringify(simulation.value.err)}`);
        }
      } catch (simError: any) {
        throw new Error(`Pre-flight simulation failed: ${simError.message}`);
      }

      // Sign and send transaction
      let signature: string;

      // For external wallets (Phantom), use separate sign/send pattern
      if (isExternalWallet && solanaWallet?.signTransaction) {
        try {
          const signedTx = await solanaWallet.signTransaction(transaction);
          signature = await connection.sendRawTransaction(signedTx.serialize(), {
            skipPreflight: true,  // Already simulated above
            preflightCommitment: 'confirmed',
            maxRetries: 3
          });
        } catch (walletError: any) {
          console.error('Wallet signing failed:', walletError);
          throw new Error(`Wallet signing failed: ${walletError.message || 'Unknown error'}`);
        }
      } else if (isEmbeddedWallet) {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: true },
          transactionOptions: {
            skipPreflight: true,  // Already simulated above
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      } else {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: false },
          transactionOptions: {
            skipPreflight: true,  // Already simulated above
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      }

      // Wait for confirmation
      const confirmed = await waitForTransactionConfirmation(connection, signature);
      if (!confirmed) {
        throw new Error('Transaction failed to confirm');
      }

      setIsLoading(false);
      return {
        success: true,
        signature
      };

    } catch (error: any) {
      console.error('❌ Distribute token failed:', error);
      const errMsg = error.message || 'Failed to distribute token on-chain';
      setError(errMsg);
      setIsLoading(false);
      return {
        success: false,
        error: errMsg
      };
    }
  }, [
    authenticated,
    solanaWallet,
    connection,
    sendTransaction,
    isEmbeddedWallet,
    isExternalWallet,
    getPDAs,
    createEd25519InstructionForResults
  ]);

  return {
    // Core functions
    launchToken,
    continueStreak, // Keep for now, will update later
    distributeToken,
    
    // Transaction state
    isLoading,
    error,
    
    // Wallet info
    walletAddress: solanaWallet?.address,
    isConnected: authenticated && !!solanaWallet?.address,
    
    // Config
    config: {
      rpcEndpoint: solanaUtils.getRpcEndpoint(),
      commitment: 'confirmed' as const,
      programId: MOON_RUNNER_PROGRAM_ID.toBase58(),
    }
  };
};

